import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Info, FileText } from 'lucide-react';
import { siteInfo } from '../data/mock';

const Disclaimer = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-b">
        <div className="container mx-auto px-4 py-8">
          <Link to="/" className="inline-flex items-center text-orange-600 hover:text-orange-700 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Disclaimer</h1>
          <p className="text-sm text-gray-600">Important information about {siteInfo.projectName}</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose prose-lg max-w-none">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg mb-8">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-yellow-900 mb-2">Important Notice</h3>
                <p className="text-sm text-yellow-800">
                  Please read this disclaimer carefully before using this website or relying on any information provided herein.
                </p>
              </div>
            </div>
          </div>

          <section className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <FileText className="w-6 h-6 text-orange-600" />
              <h2 className="text-2xl font-bold text-gray-900">General Information</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              The information contained on this website is for general information purposes only. While we endeavor to 
              keep the information up to date and correct, we make no representations or warranties of any kind, express 
              or implied, about the completeness, accuracy, reliability, suitability, or availability of the information, 
              products, services, or related graphics contained on the website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Artistic Impressions</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              All images, architectural renderings, floor plans, and artistic impressions displayed on this website are 
              for illustrative purposes only and are subject to change. The actual construction, design, layout, 
              specifications, dimensions, and features may vary from those shown.
            </p>
            <ul className="space-y-2 ml-6 text-gray-700">
              <li className="flex items-start">
                <span className="text-orange-500 mr-2">•</span>
                <span>Images are artist's impressions and not actual photographs of the project</span>
              </li>
              <li className="flex items-start">
                <span className="text-orange-500 mr-2">•</span>
                <span>Furniture, fixtures, and fittings shown are for representation only</span>
              </li>
              <li className="flex items-start">
                <span className="text-orange-500 mr-2">•</span>
                <span>Actual views, landscaping, and surroundings may differ</span>
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">RERA Compliance</h2>
            <p className="text-gray-700 leading-relaxed">
              This project is registered with Maharashtra Real Estate Regulatory Authority (MahaRERA) under 
              registration number <strong className="text-orange-600">{siteInfo.reraNumber}</strong>. 
              For more details and latest updates, please visit the official MahaRERA website: 
              <a href="https://maharera.mahaonline.gov.in" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline ml-1">
                maharera.mahaonline.gov.in
              </a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Specifications & Amenities</h2>
            <p className="text-gray-700 leading-relaxed">
              All specifications, amenities, features, and facilities mentioned on this website are indicative and 
              subject to approval from relevant authorities. {siteInfo.developer} reserves the right to make changes, 
              additions, deletions, or alterations in the specifications, designs, and plans as may be required by 
              authorities or as deemed fit by the developer.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Guarantee</h2>
            <p className="text-gray-700 leading-relaxed">
              The developer does not guarantee or warrant any appreciation in the value of the property. Past performance 
              of similar projects is not indicative of future results. Property investment is subject to market risks.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Content</h2>
            <p className="text-gray-700 leading-relaxed">
              This website may contain links to third-party websites or references to third-party services. We have no 
              control over the content and practices of these sites and cannot accept responsibility for their privacy 
              policies or content.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              In no event shall {siteInfo.developer} be liable for any loss or damage including indirect or consequential 
              loss or damage arising out of or in connection with the use of this website or reliance on the information 
              provided herein.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Verification Recommended</h2>
            <p className="text-gray-700 leading-relaxed">
              Prospective buyers are advised to independently verify all information, specifications, amenities, and 
              services before making any purchase decision. Please visit our sales office or contact our representatives 
              for the most accurate and up-to-date information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Legal Agreement</h2>
            <p className="text-gray-700 leading-relaxed">
              This disclaimer is governed by the laws of India. Any disputes arising from the use of this website or 
              the purchase of property shall be subject to the exclusive jurisdiction of courts in Thane, Maharashtra.
            </p>
          </section>

          <section className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border-2 border-orange-200">
            <div className="flex items-center space-x-3 mb-4">
              <Info className="w-6 h-6 text-orange-600" />
              <h2 className="text-2xl font-bold text-gray-900">Questions?</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this disclaimer, please contact us:
            </p>
            <div className="space-y-2">
              <p className="text-gray-800"><strong>Phone:</strong> {siteInfo.phone[0]} / {siteInfo.phone[1]}</p>
              <p className="text-gray-800"><strong>Email:</strong> {siteInfo.email}</p>
              <p className="text-gray-800"><strong>Address:</strong> {siteInfo.address}</p>
            </div>
          </section>

          <div className="mt-12 text-center text-sm text-gray-500">
            <p>© 2025 {siteInfo.developer}. All rights reserved.</p>
            <p className="mt-2">RERA: {siteInfo.reraNumber} | TMC Approved</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Disclaimer;